import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  invokeAdminRagReembed,
  loadPlatformRagSettings,
  savePlatformRagSettings,
  type PlatformRagSettings,
} from "@/lib/platformRagSettings";

export type WorkspaceOption = { id: string; label: string };

// text-embedding-3-small list price.
export const EMBED_COST_PER_MILLION_USD = 0.02;
export const COST_NOTE = `Uses Excerptus AI embedding credits (text-embedding-3-small, ~$${EMBED_COST_PER_MILLION_USD.toFixed(2)} / 1M tokens).`;

export type Estimate = {
  workspaces: number;
  docs: number;
  chunks: number;
  estUsd: number;
};

export const formatUsd = (n: number) => {
  if (n < 0.01) return "<$0.01";
  if (n < 1) return `~$${n.toFixed(3)}`;
  return `~$${n.toFixed(2)}`;
};

/** State + handlers powering the admin RAG settings tab. */
export const useRagSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PlatformRagSettings | null>(null);
  const [size, setSize] = useState(800);
  const [overlap, setOverlap] = useState(150);

  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>("");

  const [confirmWsOpen, setConfirmWsOpen] = useState(false);
  const [confirmWsText, setConfirmWsText] = useState("");
  const [wsEstimate, setWsEstimate] = useState<Estimate | null>(null);
  const [reembedSingleBusy, setReembedSingleBusy] = useState(false);

  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [confirmAllText, setConfirmAllText] = useState("");
  const [allEstimate, setAllEstimate] = useState<Estimate | null>(null);
  const [reembedAllBusy, setReembedAllBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await loadPlatformRagSettings();
        if (!alive) return;
        setSettings(s);
        setSize(s.chunk_size_tokens);
        setOverlap(s.chunk_overlap_tokens);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: docs } = await (supabase as any)
          .from("workspace_corpus_documents")
          .select("workspace_id");
        const ids = Array.from(new Set(((docs ?? []) as { workspace_id: string }[]).map((d) => d.workspace_id)));
        if (ids.length === 0) { setWorkspaces([]); return; }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: profs } = await (supabase as any)
          .from("profiles")
          .select("id, workspace_label, display_name")
          .in("id", ids);
        const opts: WorkspaceOption[] = (profs ?? []).map((p: { id: string; workspace_label: string | null; display_name: string | null }) => ({
          id: p.id,
          label: p.workspace_label ?? p.display_name ?? p.id.slice(0, 8),
        }));
        opts.sort((a, b) => a.label.localeCompare(b.label));
        if (!alive) return;
        setWorkspaces(opts);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load RAG settings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const fetchEstimate = async (scopedWorkspaceId: string | null): Promise<Estimate> => {
    const sizeTokens = settings?.chunk_size_tokens ?? size ?? 800;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let q = (supabase as any)
      .from("workspace_corpus_documents")
      .select("workspace_id, chunk_count");
    if (scopedWorkspaceId) q = q.eq("workspace_id", scopedWorkspaceId);
    const { data, error } = await q;
    if (error) throw error;
    const rows = (data ?? []) as { workspace_id: string; chunk_count: number | null }[];
    const docs = rows.length;
    const chunks = rows.reduce((acc, r) => acc + (r.chunk_count ?? 0), 0);
    const wsCount = scopedWorkspaceId ? 1 : new Set(rows.map((r) => r.workspace_id)).size;
    const effectiveChunks = chunks > 0 ? chunks : docs;
    const tokens = effectiveChunks * sizeTokens;
    const estUsd = (tokens * EMBED_COST_PER_MILLION_USD) / 1_000_000;
    return { workspaces: wsCount, docs, chunks: effectiveChunks, estUsd };
  };

  const openWsDialog = async () => {
    if (!workspaceId) { toast.error("Pick a workspace first"); return; }
    setConfirmWsOpen(true);
    setWsEstimate(null);
    try { setWsEstimate(await fetchEstimate(workspaceId)); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed to estimate cost"); }
  };

  const openAllDialog = async () => {
    setConfirmAllOpen(true);
    setAllEstimate(null);
    try { setAllEstimate(await fetchEstimate(null)); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed to estimate cost"); }
  };

  const handleSave = async () => {
    if (overlap >= size) { toast.error("Overlap must be less than chunk size"); return; }
    if (size < 200 || size > 2000) { toast.error("Chunk size must be 200–2000 tokens"); return; }
    if (overlap < 0 || overlap > 500) { toast.error("Overlap must be 0–500 tokens"); return; }
    setSaving(true);
    try {
      await savePlatformRagSettings({ chunk_size_tokens: size, chunk_overlap_tokens: overlap });
      const s = await loadPlatformRagSettings();
      setSettings(s);
      toast.success("RAG settings saved. New chunks will use these values; existing chunks won't change until re-embedded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReembedWorkspace = async () => {
    setReembedSingleBusy(true);
    try {
      const r = await invokeAdminRagReembed({ scope: "workspace", workspace_id: workspaceId });
      toast.success(`Re-embed queued: ${r.processed} processed, ${r.errors} errors`);
      setConfirmWsOpen(false);
      setConfirmWsText("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Re-embed failed");
    } finally {
      setReembedSingleBusy(false);
    }
  };

  const handleReembedAll = async () => {
    setReembedAllBusy(true);
    try {
      const r = await invokeAdminRagReembed({ scope: "all" });
      toast.success(`Re-embed complete across ${r.workspaces?.length ?? 0} workspaces · ${r.processed} processed, ${r.errors} errors`);
      setConfirmAllOpen(false);
      setConfirmAllText("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Re-embed failed");
    } finally {
      setReembedAllBusy(false);
    }
  };

  return {
    loading, saving, settings, size, setSize, overlap, setOverlap,
    workspaces, workspaceId, setWorkspaceId,
    confirmWsOpen, setConfirmWsOpen, confirmWsText, setConfirmWsText, wsEstimate, reembedSingleBusy,
    confirmAllOpen, setConfirmAllOpen, confirmAllText, setConfirmAllText, allEstimate, reembedAllBusy,
    openWsDialog, openAllDialog,
    handleSave, handleReembedWorkspace, handleReembedAll,
  };
};
